<template>
  <div class="doc-list">
    <!-- 页头 -->
    <div class="page-head">
      <div class="page-head-left">
        <h2 class="page-title">标书列表</h2>
        <p class="page-desc">全量投标项目档案，支持检索、筛选与导出</p>
      </div>
      <div class="page-head-right">
        <el-button plain icon="el-icon-download" @click="handleExport">导出数据</el-button>
        <el-button type="primary" icon="el-icon-plus" @click="handleAdd">新增标书</el-button>
      </div>
    </div>

    <div class="panel">
      <div class="panel-body">
        <!-- 筛选工具栏 -->
        <div class="filter-bar">
          <div class="filter-left">
            <el-input
              v-model.trim="search"
              placeholder="搜索项目名称 / 采购单位 / 项目编号"
              prefix-icon="el-icon-search"
              clearable
              style="width: 300px"
              @clear="handleSearch"
              @input="onSearchInput"
              @keyup.enter.native="handleSearch"
            ></el-input>
            <el-select v-model="statusFilter" placeholder="全部状态" clearable style="width: 130px" @change="handleSearch">
              <el-option label="已中标" :value="1"></el-option>
              <el-option label="未中标" :value="0"></el-option>
              <el-option label="待定" :value="2"></el-option>
            </el-select>
            <el-select v-model="methodFilter" placeholder="全部方式" clearable style="width: 130px" @change="handleSearch">
              <el-option v-for="m in methodOptions" :key="m" :label="m" :value="m"></el-option>
            </el-select>
            <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
          </div>
          <div class="filter-right">
          </div>
        </div>

        <!-- 数据表格 -->
        <el-table
          ref="bidTable"
          :data="pagedData"
          v-loading="loading"
          stripe
          size="small"
          style="width: 100%"
          class="doc-table"
        >
          <el-table-column prop="序号" label="序号" width="60" align="center">
            <template slot-scope="scope">
              <span class="seq-num">{{ scope.row['序号'] }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="项目名称" label="项目名称" min-width="240" show-overflow-tooltip>
            <template slot-scope="scope">
              <span class="project-name">{{ scope.row['项目名称'] }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="项目编号" label="项目编号" min-width="140" show-overflow-tooltip>
            <template slot-scope="scope">
              <span :class="{ 'dim-text': !scope.row['项目编号'] || scope.row['项目编号'] === '/' }">{{ scope.row['项目编号'] || '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="最高限价（预算价）万元" label="最高限价(万)" width="105" align="right">
            <template slot-scope="scope">
              <span class="limit-price">{{ scope.row['最高限价（预算价）万元'] || '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="报名截止日期" label="报名截止" width="140" show-overflow-tooltip>
            <template slot-scope="scope">
              <span class="date-cell">{{ scope.row['报名截止日期'] || '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="开标日期" label="开标日期" width="140" show-overflow-tooltip>
            <template slot-scope="scope">
              <span class="date-cell">{{ scope.row['开标日期'] || '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="采购单位" label="采购单位" min-width="200" show-overflow-tooltip>
          </el-table-column>
          <el-table-column prop="招标代理单位" label="代理单位" min-width="160" show-overflow-tooltip>
            <template slot-scope="scope">
              <span :class="{ 'dim-text': !scope.row['招标代理单位'] || scope.row['招标代理单位'] === '/' }">{{ scope.row['招标代理单位'] || '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="报价" label="报价" width="120" align="right">
            <template slot-scope="scope">
              <span class="price-cell">{{ formatMoney(scope.row['报价']) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="投标保证金" label="保证金" width="100" show-overflow-tooltip>
            <template slot-scope="scope">
              <span :class="{ 'dim-text': !scope.row['投标保证金'] || scope.row['投标保证金'] === '无' }">{{ scope.row['投标保证金'] || '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="投标单位" label="投标单位" width="90" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" :type="scope.row['投标单位'] === '云擎' ? '' : 'warning'" effect="plain">
                {{ scope.row['投标单位'] || '—' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="投标方式" label="投标方式" width="100" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row['投标方式']" size="mini" effect="light">{{ scope.row['投标方式'] }}</el-tag>
              <span v-else class="dim-text">—</span>
            </template>
          </el-table-column>
          <el-table-column prop="是否中标" label="结果" width="90" align="center">
            <template slot-scope="scope">
              <el-tag :type="bidStatusType(scope.row['是否中标'])" effect="light" size="mini">
                {{ bidStatusLabel(scope.row['是否中标']) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="备注" label="备注" min-width="110" show-overflow-tooltip>
            <template slot-scope="scope">
              <span :class="{ 'dim-text': !scope.row['备注'] }">{{ scope.row['备注'] || '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="130" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" icon="el-icon-view" @click="showDetail(scope.row)">详情</el-button>
              <el-button type="text" icon="el-icon-edit" @click="showEdit(scope.row)">编辑</el-button>
            </template>
          </el-table-column>
          <template slot="empty">
            <div class="table-empty">
              <i class="el-icon-search"></i>
              <p>未找到匹配的标书，试试调整搜索条件</p>
            </div>
          </template>
        </el-table>

        <!-- 分页 -->
        <div class="pager-wrap">
          <el-pagination
            background
            :current-page.sync="page"
            :page-size.sync="pageSize"
            :page-sizes="[10, 15, 20, 50]"
            :total="total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSearch"
            @current-change="handlePageChange"
          ></el-pagination>
        </div>
      </div>
    </div>

    <!-- 详情弹窗 -->
    <el-dialog
      :title="detailTitle"
      :visible.sync="detailVisible"
      width="820px"
      top="6vh"
      append-to-body
      custom-class="detail-dialog"
    >
      <div class="detail-grid">
        <div class="detail-item full">
          <div class="detail-label">项目名称</div>
          <div class="detail-value">{{ currentRow['项目名称'] || '—' }}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">项目编号</div>
          <div class="detail-value">{{ currentRow['项目编号'] || '—' }}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">最高限价</div>
          <div class="detail-value">{{ currentRow['最高限价（预算价）万元'] || '—' }} 万元</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">报名截止</div>
          <div class="detail-value">{{ currentRow['报名截止日期'] || '—' }}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">开标日期</div>
          <div class="detail-value">{{ currentRow['开标日期'] || '—' }}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">采购单位</div>
          <div class="detail-value">{{ currentRow['采购单位'] || '—' }}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">招标代理单位</div>
          <div class="detail-value">{{ currentRow['招标代理单位'] || '—' }}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">报价</div>
          <div class="detail-value strong">{{ formatMoney(currentRow['报价']) }}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">投标保证金</div>
          <div class="detail-value">{{ currentRow['投标保证金'] || '—' }}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">投标单位</div>
          <div class="detail-value">{{ currentRow['投标单位'] || '—' }}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">投标方式</div>
          <div class="detail-value">{{ currentRow['投标方式'] || '—' }}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">是否中标</div>
          <div class="detail-value">
            <el-tag :type="bidStatusType(currentRow['是否中标'])" effect="light" size="small">
              {{ bidStatusLabel(currentRow['是否中标']) }}
            </el-tag>
          </div>
        </div>
        <div class="detail-item full">
          <div class="detail-label">备注</div>
          <div class="detail-value">{{ currentRow['备注'] || '—' }}</div>
        </div>
      </div>

      <div class="detail-section">
        <h4>竞标单位</h4>
        <el-table :data="detailBidders" size="small" style="width: 100%">
          <el-table-column label="竞标公司" min-width="220">
            <template slot-scope="scope">
              <span class="bidder-name">
                <span class="bidder-dot" :class="{ 'is-won': scope.row.won }"></span>
                {{ scope.row.unit }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="报价" width="140" align="right">
            <template slot-scope="scope">
              <span :class="{ 'price-won': scope.row.won }">{{ scope.row.price ? formatMoney(scope.row.price) : '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="结果" width="100" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.won" type="success" size="mini" effect="light">中标</el-tag>
              <span v-else class="dim-text">—</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>

    <!-- 编辑竞标单位 -->
    <bidder-edit-dialog
      :visible.sync="editVisible"
      :project-info="editProjectInfo"
      @saved="handleSaved"
    ></bidder-edit-dialog>
  </div>
</template>

<script>
import { formatMoney, bidStatusType, bidStatusLabel } from './bidData'
import BidderEditDialog from './BidderEditDialog.vue'
import { getBidList, getBidDetail } from '@/api/document/index'

export default {
  name: 'DocumentList',
  components: { BidderEditDialog },
  data() {
    return {
      list: [],
      loading: false,
      search: '',
      statusFilter: '',
      methodFilter: '',
      page: 1,
      pageSize: 10,
      total: 0,
      detailVisible: false,
      currentRow: {},
      editVisible: false,
      editProjectInfo: { project: '', procurement: '', bidDate: '' }
    }
  },
  computed: {
    methodOptions() {
      // 从当前页 + 历史累计抽取所有出现过的"投标方式"
      const set = new Set()
      this.list.forEach(d => {
        const v = d['投标方式']
        if (v) set.add(v)
      })
      // 兜底: 业务常见方式
      const defaults = ['纸质', '电子', '纸质+电子']
      defaults.forEach(v => { if (!set.has(v)) set.add(v) })
      return Array.from(set)
    },
    pagedData() {
      // 后端已按 page/limit 及筛选条件分页, 前端直接展示
      return this.list
    },
    detailTitle() {
      return this.currentRow['项目名称'] || '项目详情'
    },
    detailBidders() {
      const row = this.currentRow
      if (!row || !row['项目名称']) return []
      const bidders = [{ unit: row['投标单位'] || '—', price: row['报价'] || '', won: row['是否中标'] === '是' }]
      for (let ci = 1; ci <= 5; ci++) {
        const comp = row['竞争公司' + ci]
        if (comp && String(comp).trim()) bidders.push({ unit: String(comp).trim(), price: '', won: false })
      }
      return bidders
    }
  },
  watch: {
    search() { this.page = 1 },
    statusFilter() { this.page = 1 },
    methodFilter() { this.page = 1 }
  },
  methods: {
    formatMoney,
    bidStatusType,
    bidStatusLabel,
    /**
     * 接口字段映射: 后端驼峰 -> 前端中文字段(保持模板与原有逻辑兼容)
     * 实际后端响应字段(基于最新截图):
     *   id            -> 序号
     *   projectName   -> 项目名称
     *   purchaser     -> 采购单位
     *   openDate      -> 开标日期
     *   signupDeadline -> 报名截止日期
     *   maxPrice      -> 最高限价(单位已是"万元", 直接保留 2 位小数)
     *   bidMethod     -> 投标方式
     *   quote         -> 报价
     *   agency        -> 招标代理单位
     *   isWon         -> 是否中标 (字符串 "1"=是, "0"=否)
     *   remark        -> 备注
     *   isVin         -> 是否有效/废标标记 (1=有效)
     * 注: 后端没有 projectNo / bidUnit 字段, 返回空字符串以兼容模板
     */
    mapRow(r) {
      if (!r) return r
      const maxPrice = r.maxPrice
      const maxPriceStr = (maxPrice === null || maxPrice === undefined || maxPrice === '')
        ? ''
        : (parseFloat(maxPrice).toFixed(2))
      return {
        '序号': r.id,
        '项目名称': r.projectName || '',
        '项目编号': r.projectNo || '/',
        '最高限价（预算价）万元': maxPriceStr,
        '报名截止日期': r.signupDeadline || '',
        '开标日期': r.openDate || '',
        '采购单位': r.purchaser || '',
        '招标代理单位': r.agency || '/',
        '报价': r.quote || '',
        '投标保证金': r.deposit || '',
        '投标单位': r.bidUnit || '',
        '投标方式': r.bidMethod || '',
        '是否中标': (r.isWin === 1 || r.isWin === '1') ? '是' : ((r.isWin === 0 || r.isWin === '0') ? '否' : ''),
        '备注': r.remark || '',
        '竞争公司1': r.competitor1 || '',
        '竞争公司2': r.competitor2 || '',
        '竞争公司3': r.competitor3 || '',
        '竞争公司4': r.competitor4 || '',
        '竞争公司5': r.competitor5 || ''
      }
    },
    // 拉取标书列表
    async fetchList() {
      this.loading = true
      try {
        const params = { page: this.page, limit: this.pageSize }
        // 关键字筛选也交给后端
        if (this.search) params.keyWord = this.search
        if (this.statusFilter !== '' && this.statusFilter !== null && this.statusFilter !== undefined) {
          params.isWin = Number(this.statusFilter)
        }
        if (this.methodFilter) params.bidMethod = this.methodFilter
        const resp = await getBidList(params)
        console.log('标书列表响应:', resp)
        if (resp && resp.code === 200) {
          // 兼容多种响应结构: {records}, {list}, {rows}, {data: [...]}, 直接数组
          let rows = []
          let total = 0
          const dataField = resp.data
          if (Array.isArray(dataField)) {
            rows = dataField
          } else if (dataField && typeof dataField === 'object') {
            rows = dataField.records || dataField.list || dataField.rows || dataField.data || []
          } else {
            rows = []
          }
          // total 从多处兜底提取:
          //   1) resp.total (顶层)
          //   2) resp.data.total / resp.data.totalCount
          //   3) resp.data.records.total 等(老接口)
          //   4) 兜底用当前页条数
          total = resp.total
            || (dataField && dataField.total)
            || (dataField && dataField.totalCount)
            || (rows && rows.length)
          this.list = rows.map(r => this.mapRow(r))
          this.total = total
          if (this.list.length === 0) {
            this.$message.warning('当前无数据或筛选条件过严')
          }
        } else {
          this.$message.error((resp && resp.msg) || '查询标书列表失败')
          this.list = []
          this.total = 0
        }
      } catch (e) {
        console.error('查询标书列表失败：', e)
        this.$message.error('查询标书列表失败')
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    /**
     * 搜索框输入防抖
     * - 400ms 内连续输入不重复请求
     * - 输入框清空时立即触发
     * - 已有定时器先清除
     */
    onSearchInput(value) {
      // 清空时立即触发
      if (!value) {
        this.handleSearch()
        return
      }
      if (this._searchTimer) {
        clearTimeout(this._searchTimer)
      }
      this._searchTimer = setTimeout(() => {
        this.page = 1
        this.fetchList()
      }, 400)
    },
    handleSizeChange() {
      this.page = 1
      this.fetchList()
    },
    handleReset() {
      this.search = ''
      this.statusFilter = ''
      this.methodFilter = ''
      this.page = 1
      this.fetchList()
    },
    handlePageChange() {
      this.fetchList()
    },
    handleSizeChange() {
      this.fetchList()
    },
    handleExport() {
      this.$message({
        type: 'info',
        message: '导出功能开发中，敬请期待'
      })
    },
    handleAdd() {
      this.$message({
        type: 'info',
        message: '新增标书功能开发中'
      })
    },
    async showDetail(row) {
      this.currentRow = row
      this.detailVisible = true
      // 优先尝试调接口拉详情(覆盖最新数据)
      if (!row || row['序号'] === undefined || row['序号'] === null) return
      try {
        const resp = await getBidDetail(row['序号'])
        if (resp && resp.code === 200) {
          // 后端返回单个对象(经过兼容)
          const detail = (resp.data && resp.data.id) ? resp.data : row
          // 合并展示: 用 mapRow 把后端驼峰字段映射成中文键
          this.currentRow = Object.assign({}, this.mapRow(detail))
        }
      } catch (e) {
        console.warn('详情接口获取失败, 使用列表数据展示', e)
      }
    },
    showEdit(row) {
      this.editProjectInfo = {
        project: row['项目名称'] || '',
        procurement: row['采购单位'] || '',
        bidDate: row['开标日期'] || ''
      }
      this.editVisible = true
    },
    handleSaved() {
      // 编辑竞标单位后刷新列表
      this.fetchList()
    }
  },
  created() {
    this.fetchList()
  },
  beforeDestroy() {
    if (this._searchTimer) {
      clearTimeout(this._searchTimer)
      this._searchTimer = null
    }
  }
}
</script>

<style scoped lang="scss">
.doc-list {
  padding: 4px 0 20px;
}

.page-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-title {
  font-size: 22px;
  font-weight: 600;
  color: #1f2d3d;
}
.page-desc {
  margin-top: 6px;
  font-size: 13px;
  color: #909399;
}

.panel {
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 12px;
  overflow: hidden;
}
.panel-body { padding: 18px 22px; }

.filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}
.filter-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.filter-right .count-tip {
  font-size: 13px;
  color: #909399;
}
.count-tip b {
  color: #409eff;
  font-size: 16px;
}

.doc-table {
  border: 1px solid #f0f2f5;
  border-radius: 8px;
}
.seq-num {
  color: #909399;
  font-variant-numeric: tabular-nums;
}
.project-name {
  color: #1f2d3d;
  font-weight: 500;
}
.limit-price {
  color: #606266;
  font-variant-numeric: tabular-nums;
}
.date-cell {
  color: #606266;
  font-size: $font-size-base;
}
.price-cell {
  color: #1f2d3d;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.dim-text {
  color: #c0c4cc;
}

.pager-wrap {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
}

.table-empty {
  padding: 36px 0;
  color: #c0c4cc;
  font-size: 13px;
}
.table-empty i {
  font-size: 36px;
  display: block;
  margin-bottom: 8px;
}

/* 详情弹窗 */
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 28px;
}
.detail-item.full { grid-column: 1 / -1; }
.detail-label {
  font-size: $font-size-base;
  color: #909399;
  margin-bottom: 4px;
}
.detail-value {
  font-size: 14px;
  color: #1f2d3d;
  word-break: break-all;
  line-height: 1.5;
}
.detail-value.strong {
  font-weight: 600;
  color: #409eff;
}
.detail-section {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #f2f3f5;
}
.detail-section h4 {
  font-size: 14px;
  font-weight: 600;
  color: #1f2d3d;
  margin-bottom: 12px;
}
.bidder-name {
  display: inline-flex;
  align-items: center;
}
.bidder-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #409eff;
  margin-right: 8px;
}
.bidder-dot.is-won { background: #36cf63; }
.price-won {
  color: #36cf63;
  font-weight: 600;
}
</style>
